var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import Big from 'big.js';
import { LRUCache } from 'lru-cache';
import * as moment from 'moment';
import * as murmur from 'murmurhash';
import { Either, EitherAsync, Left, Maybe, Nothing, Right } from 'purify-ts';
import * as api from '@codegrade/api';
import { Fraction } from '@codegrade/bigfraction';
import { assert, compare, datetime, decode, expr, maybe, object, sort, typeHelpers, } from '@codegrade/utils';
import client from '@/client';
import { NONEXISTENT } from '@/constants';
import * as models from '@/models';
import { CoursePermissions as CPerm } from '@/permissions';
import { CodeStore, CoursesStore, SiteSettingsStore, store, UserStore } from '@/store';
import * as utils from '@/utils';
import { nonenumerable, not } from '@/utils';
import { CacheDecorator } from '@/utils/cache';
import { SIMILARITY_CUTOFF, stringSimilarityFor } from '@/utils/string';
import { BaseAssignmentGradingSettings } from './assignment_grading';
import { Glob } from './glob';
import { PeerFeedbackSettings } from './peer_feedback';
import { User } from './user';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const DEFAULT_MAX_GRADE = 10;
const ALLOWED_UPDATE_PROPS = new Set(typeHelpers.keysOfFast([
    'fixed_max_rubric_points',
    'group_set',
    'division_parent_id',
    'auto_test_id',
    'peerFeedbackSettings',
    'name',
    'kind',
    'send_login_links',
    'availability',
    'gradeAvailability',
    'deadline',
    'lockDate',
    'max_grade',
    'anonymizedGrading',
    'fileToLoadFirstStr',
    'grading',
    'hasDescription',
    'hasMultipleTimeframes',
    'submissionMode',
]));
/**
 * Check that two availability values are equal.
 *
 * @param self The first value to compare.
 * @param other The other value to compare.
 * @returns If the two given values should be considered equal.
 */
function availabilityEquals(self, other) {
    return expr.match(self, 'tag', {
        fixed: ({ value: selfValue }) => other.tag === 'fixed' && selfValue === other.value,
        timed: ({ availableAt: selfAvailableAt }) => other.tag === 'timed' && selfAvailableAt.isSame(other.availableAt),
    });
}
/**
 * Check whether an assignment is available for the current user.
 *
 * @param assignment The assignment to check.
 * @param getNow A function that should return the current time, used when the availability tag
 * is "timed".
 * @returns If the assignment is available right now.
 */
export function isAssignmentAvailable(assignment, getNow) {
    return expr.match(assignment.availability, 'tag', {
        fixed: ({ value }) => value,
        timed: ({ availableAt }) => availableAt.isSameOrBefore(getNow(availableAt)),
    });
}
const anonymizeWithMurmurV3 = (() => {
    const cache = new LRUCache({ max: 3000 });
    return (key) => {
        let value = cache.get(key);
        if (value == null) {
            value = `Anonymous ${murmur.v3(key).toString(10)}`;
            cache.set(key, value);
        }
        return value;
    };
})();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class AssignmentData {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(props) {
        Object.assign(this, props);
    }
}
// eslint-disable-next-line
export class Assignment extends AssignmentData {
    /**
     * @param props The properties of the new assignment.
     */
    constructor(props) {
        super(props);
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        this._cache = new CacheDecorator();
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get canManage() {
        return this.maybeCourse.mapOrDefault(c => c.canManageAssignment, false);
    }
    get reminderTime() {
        if (this._reminderTime == null) {
            return moment.invalid();
        }
        else {
            return datetime.toMoment(this._reminderTime);
        }
    }
    get createdAt() {
        return datetime.toMoment(this._createdAt);
    }
    get coolOffPeriod() {
        return moment.duration(this._coolOffPeriod, 'seconds');
    }
    get fileToLoadFirst() {
        return this.fileToLoadFirstStr
            .chain(f => {
            try {
                return Maybe.of(Glob.make(f));
            }
            catch {
                return Nothing;
            }
        })
            .orDefaultLazy(() => Glob.default());
    }
    /**
     * Is this assignment available.
     *
     * @param getNow A function that should return the current time, used when the availability tag
     * is "timed".
     * @returns If the assignment is available right now.
     */
    getIsAvailable(getNow) {
        return isAssignmentAvailable(this, getNow);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data) {
        return new Assignment({
            _coolOffPeriod: data.coolOffPeriod.asSeconds(),
            _createdAt: data.createdAt.toISOString(),
            _reminderTime: data.reminderTime.extractNullable(),
            amount_in_cool_off_period: data.amountInCoolOffPeriod,
            analytics_workspace_ids: data.analyticsWorkspaceIds,
            auto_test_id: data.autoTestId.extractNullable(),
            cgignore: models.HandinRequirements.fromServerData(data.cgignoreVersion, data.cgignore),
            courseId: data.courseId,
            deadline: data.deadline,
            hasDescription: data.hasDescription,
            hasMultipleTimeframes: data.hasMultipleTimeframes,
            division_parent_id: data.divisionParentId.extractNullable(),
            done_email: data.doneEmail.extractNullable(),
            done_type: data.doneType.extractNullable(),
            files_upload_enabled: data.filesUploadEnabled,
            fixed_max_rubric_points: data.fixedMaxRubricPoints.extractNullable(),
            group_set: data.groupSet
                .map((groupSet) => ({
                assignment_ids: groupSet.assignmentIds,
                id: groupSet.id,
                maximum_size: groupSet.maximumSize,
                minimum_size: groupSet.minimumSize,
            }))
                .extractNullable(),
            id: data.id,
            is_lti: data.isLti,
            kind: data.kind,
            lms_name: data.lmsName.extractNullable(),
            lockDate: data.lockDate,
            max_grade: data.maxGrade,
            max_submissions: data.maxSubmissions.extractNullable(),
            name: data.name,
            peerFeedbackSettings: data.peerFeedbackSettings.map(d => new PeerFeedbackSettings(d)),
            send_login_links: data.sendLoginLinks,
            webhookConfiguration: data.webhookConfiguration,
            editor_upload_enabled: data.editorUploadEnabled,
            anonymizedGrading: data.anonymizedGrading,
            fileToLoadFirstStr: data.fileToLoadFirst,
            grading: BaseAssignmentGradingSettings.fromServerData(data.grading),
            availability: data.availability,
            gradeAvailability: data.gradeAvailability,
            submissionMode: data.submissionMode,
        });
    }
    /** @inheritdoc */
    get gradingSettings() {
        return this.grading;
    }
    /** @inheritdoc */
    get rubricRows() {
        return this.maybeRubric;
    }
    /** @inheritdoc */
    get fixedMaxRubricPoints() {
        return Maybe.fromNullable(this.fixed_max_rubric_points);
    }
    /** @inheritdoc */
    get filesUploadEnabled() {
        return this.files_upload_enabled;
    }
    /** @inheritdoc */
    get editorUploadEnabled() {
        return this.editor_upload_enabled;
    }
    /** @returns The id of the tenant that this assignment is connected to. */
    get maybeTenantId() {
        return this.maybeCourse.chain(object.attr('tenantId'));
    }
    /** @returns Wether the webhook upload type is enabled on this assignment. */
    get webhookEnabled() {
        return this.webhookConfiguration.tag === 'enabled';
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get maybeCourse() {
        return CoursesStore.getCourse()(this.courseId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get course() {
        return this.maybeCourse.extract();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get autoTestId() {
        return Maybe.fromNullable(this.auto_test_id);
    }
    get ltiProvider() {
        if (!this.is_lti) {
            return Nothing;
        }
        return this.course.ltiProvider;
    }
    get lmsName() {
        return this.ltiProvider.map(prov => prov.lms);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getReminderTimeOrDefault(now) {
        if (this.reminderTime.isValid()) {
            return this.reminderTime.clone();
        }
        const baseTime = this.deadline.caseOf({
            Just: deadline => {
                if (deadline.isBefore(now)) {
                    return now;
                }
                return deadline;
            },
            Nothing: () => now,
        });
        return baseTime.clone().add(1, 'weeks');
    }
    get created_at() {
        return datetime.formatDate(this.createdAt);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getDeadlineAsString() {
        return this.deadline.map(deadline => datetime.formatDate(deadline)).extractNullable();
    }
    // eslint-disable-next-line
    getFormattedDeadline() {
        return this.deadline
            .map(deadline => datetime.readableFormatDate(deadline))
            .extractNullable();
    }
    /**
     * The date the assignment becomes available in a readable format.
     *
     * @returns The date the assignment will become available at in a readable format. Will return
     * `Nothing` when the assignment doesn't have an available at set.
     */
    getFormattedAvailableAt() {
        return expr.match(this.availability, 'tag', {
            fixed: () => Maybe.empty(),
            timed: ({ availableAt }) => Maybe.of(datetime.readableFormatDate(availableAt)),
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getFormattedCreatedAt() {
        return datetime.readableFormatDate(this.createdAt);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    deadlinePassed(now) {
        return this.deadline.mapOrDefault(deadline => now.isAfter(deadline), false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    lockDatePassed(now) {
        return this.lockDate.mapOrDefault(lockDate => lockDate.isBefore(now), false);
    }
    get isExam() {
        return this.kind === 'exam';
    }
    /**
     * Is this an exam that hasn't started yet.
     *
     * @param now The current time to determine if this exam hasn't started yet.
     * @returns True if this is an exam, and it hasn't started yet.
     */
    isNotStartedExam(now) {
        return expr.match(this.availability, 'tag', {
            fixed: () => false,
            timed: ({ availableAt }) => this.isExam && availableAt.isAfter(now),
        });
    }
    get peerFeedbackDeadline() {
        return maybe
            .and(this.deadline, this.peerFeedbackSettings.chain(object.attr('time')))
            .map(([deadline, time]) => deadline.clone().add(time, 'seconds'))
            .extractNullable();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    peerFeedbackDeadlinePassed(now, dflt = true) {
        const deadline = this.peerFeedbackDeadline;
        if (deadline == null) {
            return dflt;
        }
        return now.isAfter(deadline);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getSubmitDisabledReasons(now) {
        if (maybe
            .and(UserStore.loggedInUser(), this.course.price)
            .mapOrDefault(([user, price]) => !user.hasPaidFor(price), false)) {
            return ['you have not paid for this course'];
        }
        const submitOwn = this.hasPermission(CPerm.can_submit_own_work);
        const submitOther = this.hasPermission(CPerm.can_submit_others_work) ||
            this.hasPermission(CPerm.can_submit_others_in_section_work);
        const res = [];
        if (!(submitOwn || submitOther)) {
            res.push('you do not have the permission to submit work for this course');
        }
        if (!this.hasPermission(CPerm.can_upload_after_deadline) && this.deadlinePassed(now)) {
            res.push("the assignment's deadline has already passed");
        }
        if (!this.hasPermission(CPerm.can_upload_after_lock_date) && this.lockDatePassed(now)) {
            res.push('the assignment is locked');
        }
        return res;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSubmitWork(now) {
        return this.getSubmitDisabledReasons(now).length === 0;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get gradeDecimalAmount() {
        switch (this.grading.scale) {
            case 'points':
                return SiteSettingsStore.getSetting()('ASSIGNMENT_POINT_DECIMALS', this.course.tenantId);
            case 'percentage':
                return SiteSettingsStore.getSetting()('ASSIGNMENT_PERCENTAGE_DECIMALS', this.course.tenantId);
            default:
                return assert.AssertionError.assertNever(this.grading);
        }
    }
    /**
     * The multiplier to determine the grade from our 10-points internal scale.
     *
     * Depends on the grading scale of the assignment only.
     */
    get gradeMultiplier() {
        if (this.grading.scale === 'points') {
            return this.grading.points.div(10);
        }
        return new Fraction(10);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    formatUnscaledGrade(grade) {
        let bigGrade;
        if (typeof grade === 'number') {
            bigGrade = new Big(grade);
        }
        else if (grade instanceof Fraction) {
            bigGrade = new Big(this.toScaledGrade(grade).valueOf());
        }
        else {
            bigGrade = grade;
        }
        const scaledString = this.toScaledGrade(bigGrade).toFixed(this.gradeDecimalAmount);
        switch (this.grading.scale) {
            case 'points':
                return scaledString;
            case 'percentage':
                return `${scaledString}%`;
            default:
                return assert.AssertionError.assertNever(this.grading);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    formatScaledGrade(grade) {
        return this.formatUnscaledGrade(this.fromScaledGrade(grade));
    }
    /**
     * The scaled grade, taking the grade scale points into consideration.
     *
     * @param grade The grade to scale.
     * @returns The scaled grade.
     */
    toScaledGrade(grade) {
        const mult = this.gradeMultiplier.valueOf();
        if (typeof grade === 'number') {
            return grade * mult;
        }
        else if (grade instanceof Fraction) {
            return grade.mul(this.gradeMultiplier);
        }
        else {
            return grade.mul(mult);
        }
    }
    /**
     * The unscaled grade, not taking the max grade and scale into consideration.
     *
     * ATTENTION! If the scale is set to 0 points, the return will always be zero.
     *
     * @param grade The grade to unscale.
     * @returns The unscaled grade.
     */
    fromScaledGrade(grade) {
        const mult = this.gradeMultiplier.valueOf();
        if (mult === 0) {
            return 0;
        }
        else if (typeof grade === 'number') {
            return grade / mult.valueOf();
        }
        else if (grade instanceof Fraction) {
            return grade.div(this.gradeMultiplier);
        }
        else {
            return grade.div(mult.valueOf());
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get scaledMaxGrade() {
        return this.toScaledGrade(this.max_grade);
    }
    get graders() {
        if (this.graderIds == null) {
            return null;
        }
        return object.filterMap(this.graderIds, id => {
            const user = User.findUserById(id);
            return Maybe.fromNullable(user);
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    hasPermission(permission) {
        return this.maybeCourse.mapOrDefault(c => c.hasPermission(permission), false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _canSeeFeedbackType(typ) {
        if (this.gradeAvailability.value) {
            return true;
        }
        const perm = {
            grade: CPerm.can_see_grade_before_open,
            linter: CPerm.can_see_linter_feedback_before_done,
            user: CPerm.can_see_user_feedback_before_done,
            peer: CPerm.can_see_peer_feedback_before_done,
        }[typ];
        if (perm == null) {
            throw new Error(`Requested feedback type "${typ}" is not known.`);
        }
        return this.hasPermission(perm);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSeeGrade() {
        return this._canSeeFeedbackType('grade');
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canEditGrade() {
        return this.hasPermission(CPerm.can_grade_work);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSeeAllInlineFeedback() {
        const res = this._canSeeFeedbackType('user');
        return this.peerFeedbackSettings.caseOf({
            Just: () => res && this._canSeeFeedbackType('peer'),
            Nothing: () => res,
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSeeLinterFeedback() {
        return this._canSeeFeedbackType('linter');
    }
    get rubric() {
        return store.getters['rubrics/rubrics'][this.id];
    }
    get maybeRubric() {
        return Maybe.fromNullable(this.rubric).chain(rubric => rubric === NONEXISTENT ? Maybe.empty() : Maybe.of(rubric));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    update(newProps) {
        const propKeys = Object.keys(newProps);
        if (!propKeys.every(key => ALLOWED_UPDATE_PROPS.has(key))) {
            const disallowedPairs = propKeys
                .filter(key => !ALLOWED_UPDATE_PROPS.has(key))
                .map(key => `${key}: ${newProps[key]}`)
                .join(', ');
            throw TypeError(`Cannot set assignment property: ${disallowedPairs}`);
        }
        return new Assignment({ ...this, ...newProps });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    diff(other, props) {
        return props
            .filter((prop) => {
            const EQ = compare.Order.EQ;
            switch (prop) {
                case 'grading':
                    return !this[prop].equal(other[prop]);
                case 'availability':
                    return !availabilityEquals(this[prop], other[prop]);
                case 'gradeAvailability':
                    return this[prop].value !== other[prop].value;
                default: {
                    return compare.compareNullable(this[prop], other[prop]) !== EQ;
                }
            }
        })
            .reduce((acc, prop) => {
            acc[prop] = this[prop];
            return acc;
        }, {});
    }
    /**
     * Sort assignments with more similar names to this assignment first.
     *
     * When similarities are the same we do the same with their course names,
     * and otherwise sort newer assignments first.
     *
     * @param assignments The assignments to sort.
     * @param getCourseName Set the name of the course of an assignment.
     * @param filter Function to filter out assignments.
     * @returns The sorted and filtered assignments.
     */
    _sortAssignmentsForImport(assignments, 
    /** Get the name of the course of an assignment. */
    getCourseName, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    filter = () => true) {
        const getAssigSimilarity = stringSimilarityFor(this.name);
        const getCourseSimilarity = this.maybeCourse
            .map(course => stringSimilarityFor(course.name))
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            .map(getter => (assig) => getCourseName(assig).map(getter));
        const cutoff = SIMILARITY_CUTOFF;
        return sort.sortBy(assignments.filter(assignment => assignment.id !== this.id && filter(assignment)), assig => {
            const assigSimil = getAssigSimilarity(assig.name);
            const courseSimil = getCourseSimilarity.chain(getter => getter(assig)).orDefault(1);
            let similarity = (assigSimil + courseSimil) / 2;
            // Don't use the similarity as sort key when they are not
            // really that similar.
            if (similarity < cutoff) {
                similarity = 0;
            }
            return [similarity, assig.createdAt];
        }, 
        // Get most similar and newest assignments at the top.
        { reverse: true });
    }
    /**
     * Sort assignments with more similar names to this assignment first.
     *
     * When similarities are the same we do the same with their course names,
     * and otherwise sort newer assignments first.
     *
     * @param assignments The assignments to sort.
     * @param filter Function to filter out assignments.
     * @returns The sorted and filtered assignments.
     */
    sortAssignmentsForImport(assignments, 
    /** @returns By default always allow all assignments. */
    filter = () => true) {
        return this._sortAssignmentsForImport(assignments, assig => assig.maybeCourse.map(object.attr('name')), filter);
    }
    /**
     * Sort assignments with more similar names to this assignment first.
     *
     * When similarities are the same we do the same with their course names,
     * and otherwise sort newer assignments first.
     *
     * @param assignments The assignments to sort.
     * @param getCourseName Set the name of the course of an assignment.
     * @param filter Function to filter out assignments.
     * @returns The sorted and filtered assignments.
     */
    sortApiAssignmentsForImport(assignments, getCourseName, 
    /** @returns By default always allow all assignments. */
    filter = () => true) {
        return this._sortAssignmentsForImport(assignments, assig => Maybe.of(getCourseName(assig)), filter);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get canBeCopiedInto() {
        return [
            CPerm.can_edit_cgignore,
            CPerm.can_edit_assignment_info,
            CPerm.manage_rubrics,
            CPerm.can_edit_autotest,
            CPerm.can_edit_group_set,
        ].every(perm => this.hasPermission(perm));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    loadDescription() {
        if (!this.hasDescription) {
            return EitherAsync.liftEither(Either.of(''));
        }
        return client.assignment
            .getDescription({ assignmentId: this.id })
            .chain(async ({ cgResult }) => decode.bufferE(new Uint8Array(cgResult)));
    }
    /** @returns The anonymization method to use, if any. */
    get _anonymizationMethod() {
        return this.anonymizedGrading.map(algorithm => expr.switcher(algorithm, {
            murmur_v3: () => 
            /**
             * The actual anonymization method to use for the assignment.
             *
             * @param id The id to hash.
             * @returns An anonymized name.
             */
            (id) => anonymizeWithMurmurV3(`${id} ${this.id}`),
        }));
    }
    /** @returns Maybe the anonymization method to use for the current user. */
    get anonymizationMethod() {
        return this._anonymizationMethod.filter(() => !this.hasPermission(CPerm.can_see_anonymized_names));
    }
    /**
     * Get the name of the given section as it should be displayed in this assignment.
     *
     * @param user The user for which the section is displayed. If this is the same as the current
     * user the actual section name is used.
     * @param section The section that you want to display.
     * @returns The (maybe anonymized) name of the given section.
     */
    getSectionName(user, section) {
        const loggedInUser = UserStore.loggedInUser();
        return this.anonymizationMethod
            .filter(() => !loggedInUser.mapOrDefault(u => user.isEqualOrMemberOf(u.user), false))
            .caseOf({
            Just: method => method(section.id),
            Nothing: () => section.name,
        });
    }
    /**
     * Sort the given assignments.
     *
     * They are sorted based on their name (case insensitively), if the name is the same the id is
     * used to always provide a stable sort.
     *
     * @param assignments The assignments you want to sort.
     * @returns A fresh array that contains the given assignments, but sorted.
     */
    static sortAssignments(assignments) {
        return sort.sortBy(assignments, assig => [assig.name, assig.id], {
            caseInsensitive: true,
        });
    }
    /** @returns The file tree of the editor template of this assignment. */
    loadEditorTemplate() {
        return client.assignment.getTemplate({ assignmentId: this.id }).chain(response => EitherAsync.liftEither(response.mapEither(({ files }) => files.caseOf({
            Nothing: () => Right(Maybe.empty()),
            Just: f => {
                if (f instanceof api.DirectoryWithChildren) {
                    return Right(Maybe.of(models.Directory.fromServerData(f, null)));
                }
                else {
                    return Left(Error('Got invalid data.'));
                }
            },
        }))));
    }
    /**
     * Get the contents of a single editor template file.
     *
     * @param file The metadata of the file to load.
     * @returns The requested file data, if it exists, or an error.
     */
    loadEditorTemplateFile(file) {
        return EitherAsync.fromPromise(() => CodeStore.loadCodeFromRoute({
            route: [
                'api',
                'v1',
                'assignments',
                this.id.toString(10),
                'template',
                file.id.toString(10),
            ],
            hash: file.hash,
        }).then(utils.responseWithValue, Left));
    }
}
__decorate([
    nonenumerable
], Assignment.prototype, "_cache", void 0);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "reminderTime", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "createdAt", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "coolOffPeriod", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "fileToLoadFirst", null);
__decorate([
    nonenumerable
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "ltiProvider", null);
__decorate([
    nonenumerable
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "lmsName", null);
__decorate([
    nonenumerable
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
], Assignment.prototype, "created_at", null);
__decorate([
    nonenumerable
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "isExam", null);
__decorate([
    nonenumerable
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "peerFeedbackDeadline", null);
__decorate([
    nonenumerable
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "graders", null);
__decorate([
    nonenumerable
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "rubric", null);
__decorate([
    nonenumerable
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Assignment.prototype, "maybeRubric", null);
__decorate([
    CacheDecorator.cache
], Assignment.prototype, "_anonymizationMethod", null);
/** Class used to retrieve the permissions and capabilities for each assignment. */
export class AssignmentCapabilities {
    /** @param assignment The assignment retrieved from the store. */
    constructor(assignment) {
        this.assignment = Maybe.fromNullable(assignment);
    }
    /**
     * Returns the chosen property from the lti provider connected to this assignment
     * if the assignment and the provider are available.
     *
     * @param feature The feature to retrieve from the connected lti provider.
     * @returns The chosen LTI provider property if a provider is available.
     */
    getLtiProp(feature) {
        return this.ltiProvider.map(prov => prov[feature]);
    }
    /** @returns The lti provider connected to the assignment. */
    get ltiProvider() {
        return this.assignment.chain(object.attr('ltiProvider'));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get canEditState() {
        return this.canEditInfo;
    }
    /** @returns Whether the User has permission to edit the information of the assignment. */
    get canEditInfo() {
        return this.assignment.mapOrDefault(a => a.hasPermission(CPerm.can_edit_assignment_info), false);
    }
    /** @returns Whether the User has permission to edit the name of the assignment. */
    get canEditName() {
        return this.canEditInfo && this.getLtiProp('supportsName').mapOrDefault(not, true);
    }
    /** @returns Whether the User has permission to edit the deadline of the assignment. */
    get canEditDeadline() {
        return this.canEditInfo && this.getLtiProp('supportsDeadline').mapOrDefault(not, true);
    }
    /** @returns Whether the User has permission to edit the lockdate of the assignment. */
    get canEditLockDate() {
        if (!this.canEditInfo) {
            return false;
        }
        return this.ltiProvider.caseOf({
            Just: prov => {
                if (!prov.supportsLockDate) {
                    return true;
                }
                return !SiteSettingsStore.getSetting()('LTI_LOCK_DATE_COPYING_ENABLED', this.assignment.chain(object.attr('maybeCourse')).chain(c => c.tenantId));
            },
            Nothing: () => true,
        });
    }
    /**
     * Returns whether the user is granted a determined permission on this assignment if the
     * assignment was loaded. Defaults to false otherwise.
     *
     * @param permission The permission to look up.
     * @returns The result from the permission lookup if the assignment is loaded, false otherwise.
     */
    hasPermission(permission) {
        return this.assignment.mapOrDefault(a => a.hasPermission(permission), false);
    }
    /** @returns Whether the User has permission to edit the availability of the assignment. */
    get canEditAvailableAt() {
        return (this.canEditInfo &&
            this.getLtiProp('supportsAvailableAtManagement').mapOrDefault(not, true));
    }
    /** @returns Whether the User has permission to edit the max grade of the assignment. */
    get canEditMaxGrade() {
        return (this.hasPermission(CPerm.can_edit_maximum_grade) &&
            this.getLtiProp('supportsBonusPoints').orDefault(true));
    }
    /** @returns Whether the current user has permission to see all this assignment's timeframes. */
    get canViewTimeframes() {
        return this.hasPermission(CPerm.can_view_all_assignment_timeframes);
    }
    /** @returns Whether the User has permission to edit the submission settings of the assignment. */
    get canEditSubmissionSettings() {
        return this.canEditInfo;
    }
    /** @returns Whether the User has permission to update their grader status in assignment. */
    get canUpdateGraderStatus() {
        return this.hasPermission(CPerm.can_grade_work) || this.canUpdateOtherGraderStatus;
    }
    /** @returns Whether the User has permission to update other graders' status in the assignment. */
    get canUpdateOtherGraderStatus() {
        return this.hasPermission(CPerm.can_update_grader_status);
    }
}
